import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  ChartPieSlice,
  ArrowsLeftRight,
  Bank,
  UsersThree,
  CalendarCheck,
  ArrowsClockwise,
  Target,
  UsersFour,
  Sparkle,
  SignOut,
  Vault,
  Bell,
  BellRinging,
  SpeakerHigh,
  SpeakerSlash,
  CheckCircle,
  PaperPlaneTilt,
  Warning,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import api from "@/lib/api";
import { formatMoney } from "@/lib/format";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "az önce";
  if (diff < 3600) return `${Math.floor(diff / 60)} dk önce`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} sa önce`;
  return `${Math.floor(diff / 86400)} gün önce`;
}

function playBeep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine"; o.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
    o.start(); o.stop(ctx.currentTime + 0.31);
    o.onended = () => ctx.close();
  } catch (e) {}
}

function notifyDesktop(count) {
  try {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification("BEYT KASA", { body: `${count} yeni personel kaydı girildi.` });
    }
  } catch (e) {}
}

const NAV = [
  { to: "/", label: "Panel", icon: ChartPieSlice, end: true, id: "dashboard" },
  { to: "/transactions", label: "Gelir & Gider", icon: ArrowsLeftRight, id: "transactions" },
  { to: "/accounts", label: "Banka Hesapları", icon: Bank, id: "accounts" },
  { to: "/kasa-detay", label: "Kasa Detay", icon: Vault, id: "kasa-detay" },
  { to: "/contacts", label: "Cari Hesaplar", icon: UsersThree, id: "contacts" },
  { to: "/payments", label: "Ödeme & Tahsilat", icon: CalendarCheck, id: "payments" },
  { to: "/recurring", label: "Sabit Ödemeler", icon: ArrowsClockwise, id: "recurring" },
  { to: "/budgets", label: "Bütçe Hedefleri", icon: Target, id: "budgets" },
  { to: "/staff", label: "Personel", icon: UsersFour, id: "staff" },
  { to: "/reports", label: "Raporlar & AI", icon: Sparkle, id: "reports" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { display, changeDisplay, currencies, rateInfo } = useCurrency();
  const navigate = useNavigate();

  const [notifCount, setNotifCount] = useState(0);
  const [notifItems, setNotifItems] = useState([]);
  const [soundOn, setSoundOn] = useState(localStorage.getItem("bk_notif_sound") !== "false");
  const lastSeenRef = useRef(localStorage.getItem("bk_notif_seen") || new Date(Date.now() - 7 * 864e5).toISOString());
  const prevCountRef = useRef(0);
  const soundRef = useRef(soundOn);
  soundRef.current = soundOn;

  const poll = useCallback(() => {
    api.get(`/notifications?since=${encodeURIComponent(lastSeenRef.current)}`)
      .then((r) => {
        const c = r.data.count;
        if (c > prevCountRef.current && soundRef.current) { playBeep(); notifyDesktop(c); }
        prevCountRef.current = c;
        setNotifCount(c);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 20000);
    return () => clearInterval(t);
  }, [poll]);

  const toggleSound = () => {
    const v = !soundOn;
    setSoundOn(v);
    localStorage.setItem("bk_notif_sound", v ? "true" : "false");
    if (v && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  };

  const openNotifs = (open) => {
    if (open) {
      api.get(`/notifications?since=${encodeURIComponent(new Date(Date.now() - 30 * 864e5).toISOString())}`)
        .then((r) => setNotifItems(r.data.items))
        .catch(() => {});
      const now = new Date().toISOString();
      lastSeenRef.current = now;
      localStorage.setItem("bk_notif_seen", now);
      prevCountRef.current = 0;
      setNotifCount(0);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-border bg-card/40 fixed h-screen">
        <div className="px-6 h-16 flex items-center gap-2.5 border-b border-border">
          <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center">
            <Vault size={20} weight="fill" className="text-primary-foreground" />
          </div>
          <div>
            <div className="font-heading font-semibold tracking-tight leading-none">BEYT KASA</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mt-0.5">Finans Yönetimi</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.id}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-muted-foreground hover:bg-white/5 hover:text-foreground"
                }`
              }
            >
              <item.icon size={20} weight="regular" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold text-primary">
              {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="text-muted-foreground hover:text-destructive transition-colors"
              title="Çıkış"
            >
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-64 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-background/60 backdrop-blur-xl sticky top-0 z-20 flex items-center justify-between px-4 md:px-8">
          <div className="md:hidden flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
              <Vault size={18} weight="fill" className="text-primary-foreground" />
            </div>
            <span className="font-heading font-semibold">BEYT KASA</span>
          </div>
          <div className="hidden md:block" />
          <div className="flex items-center gap-3">
            <button onClick={toggleSound} data-testid="notif-sound-toggle" title={soundOn ? "Bildirim sesi açık" : "Bildirim sesi kapalı"}
              className={`p-1.5 transition-colors ${soundOn ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
              {soundOn ? <SpeakerHigh size={19} /> : <SpeakerSlash size={19} />}
            </button>
            <Popover onOpenChange={openNotifs}>
              <PopoverTrigger asChild>
                <button className="relative text-muted-foreground hover:text-foreground transition-colors p-1.5" data-testid="notif-bell" title="Bildirimler">
                  {notifCount > 0
                    ? <BellRinging size={20} weight="fill" className="text-primary" />
                    : <Bell size={20} weight="regular" />}
                  {notifCount > 0 && (
                    <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-white text-[10px] font-semibold flex items-center justify-center">
                      {notifCount > 9 ? "9+" : notifCount}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0 bg-card border-border">
                <div className="px-4 py-3 border-b border-border">
                  <div className="font-heading text-sm font-medium">Bildirimler</div>
                  <div className="text-xs text-muted-foreground">Nakit uyarıları ve personel kayıtları</div>
                </div>
                <div className="max-h-96 overflow-y-auto" data-testid="notif-list">
                  {notifItems.length === 0 ? (
                    <p className="text-sm text-muted-foreground p-6 text-center">Yeni bildirim yok.</p>
                  ) : notifItems.map((n, i) => (
                    <div key={i} className={`flex items-start gap-3 px-4 py-3 border-b border-border/60 hover:bg-white/5 transition-colors ${n.risk ? "bg-destructive/5" : ""}`}>
                      {n.risk
                        ? <Warning size={18} weight="fill" className="text-destructive mt-0.5 shrink-0" />
                        : <CheckCircle size={18} className="text-[#10B981] mt-0.5 shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm truncate ${n.risk ? "text-destructive" : ""}`}>{n.label}</div>
                        <div className="text-xs text-muted-foreground">
                          {n.risk ? <span className="text-destructive">Nakit Uyarısı</span> : <><span className="text-primary">{n.staff}</span> · {n.kind}</>} · {timeAgo(n.created_at)}
                        </div>
                      </div>
                      {n.amount != null && <div className="font-mono-num text-xs whitespace-nowrap">{formatMoney(n.amount, n.currency, currencies)}</div>}
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            {rateInfo?.updated_at && (
              <div className="hidden lg:flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="live-rate-badge" title={`Kaynak: ${rateInfo.source}`}>
                <span className="h-1.5 w-1.5 rounded-full bg-[#10B981] animate-pulse" />
                Canlı kur
              </div>
            )}
            <span className="text-xs uppercase tracking-[0.1em] text-muted-foreground hidden sm:inline">Para Birimi</span>
            <Select value={display} onValueChange={changeDisplay}>
              <SelectTrigger className="w-[130px] h-9 bg-secondary border-border" data-testid="currency-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {currencies.map((c) => (
                  <SelectItem key={c.code} value={c.code} data-testid={`currency-opt-${c.code}`}>
                    {c.symbol} {c.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </header>

        {/* Mobile nav */}
        <nav className="md:hidden flex overflow-x-auto gap-1 px-3 py-2 border-b border-border bg-card/40">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 px-3 py-1.5 rounded-md text-[10px] whitespace-nowrap transition-colors ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`
              }
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <main className="flex-1 p-4 md:p-8 max-w-[1500px] w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
